import { PatientSingleResponse, IPatient, IPatientContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, Patient } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentPatientContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const patientContext: IPatientContext = useMemo(() => {
        const patient = consentEventContext.data?.patient as IPatient;
        const error = consentEventContext.error;

        const patientMutate: KeyedMutator<PatientSingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: PatientSingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { patient, responseStatus: error } as PatientSingleResponse;

                const callback = (data as MutatorCallback<PatientSingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<PatientSingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<PatientSingleResponse>);
            }

            if (!newSingleData) {
                newData.patient = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.patient = newSingleData.patient as Patient;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    patient: updatedData.patient,
                    responseStatus: updatedData.responseStatus
                } as PatientSingleResponse
            }

            return undefined;
        }

        const patientRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.patient;
        }

        return {
            data: patient,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: patientMutate,
            refresh: patientRefresh
        }
    }, [consentEventContext])

    return patientContext
}

export default useGenerateEConsentPatientContext;